
import { ArrowRight, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Member } from "@/types/member";
import { Settlement } from "@/types/payment";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import { useTranslation } from "react-i18next";

interface SettlementItemProps {
  settlement: Settlement;
  members: Member[];
  currencies: {
    code: string;
    symbol: string;
  }[];
  isUserPayment: boolean;
  onSettlePayment?: (settlement: Settlement) => void;
}

export const SettlementItem = ({
  settlement,
  members,
  currencies,
  isUserPayment,
  onSettlePayment
}: SettlementItemProps) => {
  const { t, i18n } = useTranslation();
  const fromMember = members.find(m => m.id === settlement.from);
  const toMember = members.find(m => m.id === settlement.to);

  const getBgClass = () => {
    if (settlement.isPaid) {
      return isUserPayment ? "bg-[#5770fd]/10 border border-[#5770fd]/20"
      : "bg-white/50 dark:bg-black/50 border border-[#5770fd]/20";
    } else {
      return isUserPayment ? "bg-destructive/10 border border-destructive/20"
      : "bg-white/50 dark:bg-black/50";
    }
  };

  // Format date based on current language
  const formatSettlementDate = (date: string | undefined) => {
    if (!date) return '';
    
    try {
      const dateObj = new Date(date);
      // Use Japanese locale for Japanese language
      if (i18n.language === 'ja') {
        return format(dateObj, 'MM月dd日', { locale: ja });
      } 
      // Use standard format for other languages (English)
      return format(dateObj, 'MMM d');
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  };

  return <div className={`rounded-lg backdrop-blur-sm animate-fade-in ${getBgClass()}`}>
      <div className="px-3 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <Avatar className="h-5 w-5 flex-shrink-0">
                <AvatarImage src={fromMember?.avatar_url || undefined} />
                <AvatarFallback>{fromMember?.name?.charAt(0).toUpperCase() || '?'}</AvatarFallback>
              </Avatar>
              <div className="truncate">
                <span className="text-xs font-medium">{fromMember?.name || t('groups.unnamed')}</span>
              </div>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <div className="flex items-center gap-1.5 min-w-0">
              <Avatar className="h-5 w-5 flex-shrink-0">
                <AvatarImage src={toMember?.avatar_url || undefined} />
                <AvatarFallback>{toMember?.name?.charAt(0).toUpperCase() || '?'}</AvatarFallback>
              </Avatar>
              <div className="truncate">
                <span className="text-xs font-medium">{toMember?.name || t('groups.unnamed')}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <Badge variant={settlement.isPaid ? "success" : isUserPayment ? "destructive" : "secondary"} className="text-[12px] h-6 px-2.5">
              {currencies.find(c => c.code === settlement.currency)?.symbol || '¥'}
              {Math.round(settlement.amount).toLocaleString()}
            </Badge>
            {settlement.isPaid && <div className="flex items-center mt-1">
                <Check className="h-3.5 w-3.5 text-green-500 mr-1" />
                <span className="text-[10px] text-green-600">
                  {t('settlements.paid')} {settlement.paidAt ? formatSettlementDate(settlement.paidAt) : ''}
                </span>
              </div>}
          </div>
        </div>
        {isUserPayment && !settlement.isPaid && onSettlePayment && <div className="mt-3 flex justify-center w-full">
            <Button 
              size="sm" 
              onClick={() => onSettlePayment(settlement)} 
              className="h-8 text-xs px-6 mx-auto"
              fullWidth
              rounded="full"
            >
              {t('settlements.settlePayment')}
            </Button>
          </div>}
      </div>
    </div>;
};
